'use client';

import { cn } from '@core/utils';
import { useEffect, useState } from 'react';
import CountUp from 'react-countup';

import { useSubscribedState } from '@/hooks/useSubscribedState';

import styles from './AnimatedNumber.module.scss';

type Direction = 'up' | 'down';

interface AnimatedNumberProps {
  value: number;
  direction: Direction;
  className?: string;
  suffix?: string;
  animate?: boolean;
}

const rand = (min: number, max: number) => Math.random() * (max - min) + min;

const format = (n: number) =>
  Math.max(0, Math.round(n / 100) * 100).toLocaleString('ko-KR');

const CONFIG: Record<
  Direction,
  { interval: [number, number]; delta: [number, number]; duration: number }
> = {
  up: { interval: [800, 2000], delta: [100, 800], duration: 0.6 },
  down: { interval: [1500, 4000], delta: [100, 500], duration: 0.8 },
};

export const AnimatedNumber = ({
  value,
  direction,
  className,
  suffix = '',
  animate = true,
}: AnimatedNumberProps) => {
  const safeValue = Math.max(0, value);
  const [counter, setCounter] = useSubscribedState(
    { start: safeValue, end: safeValue },
    [safeValue],
  );
  const [pulse, setPulse] = useState(false);
  const config = CONFIG[direction];

  useEffect(() => {
    if (!animate) return;

    let id: NodeJS.Timeout;

    const tick = () => {
      id = setTimeout(
        () => {
          setCounter((prev) => {
            const delta = Math.floor(rand(...config.delta)) * 100;
            const newEnd =
              direction === 'up'
                ? prev.end + delta
                : Math.max(0, prev.end - delta);

            return { start: prev.end, end: newEnd };
          });
          setPulse(true);
          tick();
        },
        rand(...config.interval),
      );
    };

    tick();

    return () => clearTimeout(id);
  }, [direction, config, animate, setCounter]);

  return (
    <span className={className}>
      <span
        className={cn('tabular-nums', styles.number, pulse && styles.pulse)}
        onAnimationEnd={() => setPulse(false)}
      >
        <CountUp
          start={counter.start}
          end={counter.end}
          duration={config.duration}
          preserveValue
          formattingFn={format}
        />
      </span>
      {suffix}
    </span>
  );
};
